import { useFormContext, Controller } from 'react-hook-form';
// @mui
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import { useState } from 'react';

// ----------------------------------------------------------------------


interface RHFAutocompleteExtra<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'options'> {
  options?: FreeSolo extends true ? undefined : readonly T[];
  name: string;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  required?: boolean;
}

type Props<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = RHFAutocompleteExtra<T, Multiple, DisableClearable, FreeSolo>;


export default function RHFAutocomplete<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  name,
  label,
  placeholder,
  helperText,
  required = false,
  renderInput,
  ...other
}: Props<T, Multiple, DisableClearable, FreeSolo> & RHFAutocompleteExtra<T, Multiple, DisableClearable, FreeSolo>) {
  const { control, setValue, getValues } = useFormContext();
  const [inputValue, setInputValue] = useState<string>('');



  const onPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
      const pasteData = event.clipboardData?.getData('text');
      if (!pasteData) {
          return;
      }
      event.preventDefault();
      const pasteItems = pasteData
          .split('\n')
          .map((item: string) => item.trim())
          .filter(Boolean);
      setValue(name as any, Array.from(new Set([...getValues(name), ...pasteItems])));
  };

  const extraProps = {...other} as Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'>

  if (!!extraProps.freeSolo) {
    extraProps.options = extraProps.options || getValues(name);
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required }}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          onChange={(event, newValue) => setValue(name, newValue, { shouldValidate: true })}
          renderInput={(params) => renderInput ? renderInput(Object.assign(params, { onPaste } as any)) : (
            <TextField
              label={label}
              placeholder={placeholder}
              error={!!error}
              helperText={error ? error?.message : helperText}
              onPaste={onPaste}
              {...params}
            />
          )}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
          onKeyDown={(event) => {
            if (other.freeSolo) {
              if (event.key === 'Enter' && inputValue.trim() !== '') {
                let updatedValue = getValues(name);
                if (other.multiple) {
                  updatedValue = Array.from(new Set([...updatedValue, inputValue.trim()]));
                } else {
                  updatedValue = inputValue.trim();
                }
                event.preventDefault();
                event.defaultMuiPrevented = true;
                field.onChange(updatedValue);
                setInputValue('');
              }
            }
          }}
          {...extraProps}
        />
      )}
    />
  );
}
